import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Loader2, X, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import { generateHMACSignature, generateRandomHex, generateUUID } from './utils';
import type { Site } from './types';

interface ConnectSiteModalProps {
  show: boolean;
  editingSite?: Site | null;
  onClose: () => void;
  onSiteConnected: (site: Site) => void;
  onSiteUpdated: (siteId: string, updates: Partial<Site>) => void;
}

export const ConnectSiteModal: React.FC<ConnectSiteModalProps> = ({
  show,
  editingSite,
  onClose,
  onSiteConnected,
  onSiteUpdated,
}) => {
  const { currentWorkspace } = useWorkspace();
  const [siteUrl, setSiteUrl] = useState(editingSite?.url || '');
  const [publicKey, setPublicKey] = useState(generateUUID());
  const [secretKey, setSecretKey] = useState(generateRandomHex(64));
  const [showSecret, setShowSecret] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; data?: any } | null>(null);

  const regenerateKeys = () => {
    setPublicKey(generateUUID());
    setSecretKey(generateRandomHex(64));
  };

  const handleCopyKey = (text: string, btnId: string) => {
    navigator.clipboard.writeText(text);
    const btn = document.getElementById(btnId);
    if (btn) {
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-emerald-500"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      setTimeout(() => {
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground hover:text-foreground transition-colors"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
      }, 2000);
    }
  };

  const handleTestConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteUrl || !publicKey || !secretKey) {
      setTestResult({ success: false, message: 'Please fill out all fields.' });
      return;
    }
    if (!currentWorkspace?.id) {
      setTestResult({ success: false, message: 'You must be in a workspace to connect a site.' });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      let formattedUrl = siteUrl.trim();
      if (!formattedUrl.startsWith('http')) formattedUrl = 'https://' + formattedUrl;
      formattedUrl = formattedUrl.replace(/\/+$/, '');

      const endpoint = `${formattedUrl}/wp-json/agencyplus/v1/handshake`;
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const body = JSON.stringify({ ping: 'test' });
      const canonicalString = `${timestamp}\nPOST\n/agencyplus/v1/handshake\n${body}`;
      const signature = await generateHMACSignature(secretKey, canonicalString);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AgencyPlus-Public-Key': publicKey,
          'X-AgencyPlus-Signature': signature,
          'X-AgencyPlus-Timestamp': timestamp,
        },
        body,
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success) {
        const dbSite = {
          workspace_id: currentWorkspace.id,
          name: data.site_name || 'My WordPress Site',
          url: formattedUrl,
          status: 'connected',
          wp_version: data.wordpress_version || 'Unknown',
          public_key: publicKey,
          secret_key: secretKey,
          last_ping: data.connected_at || new Date().toISOString(),
        };

        if (editingSite) {
          const { error: updateError } = await supabase
            .from('workspace_sites')
            .update(dbSite)
            .eq('id', editingSite.id);

          if (updateError) throw new Error('Failed to update site credentials: ' + updateError.message);

          setTestResult({ success: true, message: 'Connection successful! Site is now verified and linked.', data });

          onSiteUpdated(editingSite.id, {
            name: dbSite.name,
            url: dbSite.url,
            status: dbSite.status as any,
            lastPing: dbSite.last_ping,
            wpVersion: dbSite.wp_version,
            publicKey: dbSite.public_key,
            secretKey: dbSite.secret_key,
          });

          setTimeout(() => onClose(), 2000);
          return;
        }

        const { data: insertedData, error: insertError } = await supabase
          .from('workspace_sites')
          .insert(dbSite)
          .select()
          .single();

        if (insertError) throw new Error('Site connected successfully, but failed to save: ' + insertError.message);

        setTestResult({ success: true, message: 'Connection successful! Site is now linked.', data });

        if (insertedData) {
          const newSite: Site = {
            id: insertedData.id,
            name: insertedData.name,
            url: insertedData.url,
            status: insertedData.status,
            lastPing: insertedData.last_ping,
            wpVersion: insertedData.wp_version,
            publicKey: insertedData.public_key,
            secretKey: insertedData.secret_key,
            workspace_id: insertedData.workspace_id,
            created_at: insertedData.created_at,
            thumbnail_url: insertedData.thumbnail_url,
            uptimeStatus: 'unknown',
          };
          onSiteConnected(newSite);

          generateThumbnailAsync(formattedUrl, insertedData.id, currentWorkspace.id);
        }

        setTimeout(() => onClose(), 2000);
      } else {
        setTestResult({ success: false, message: data?.message || `HTTP Error ${response.status}`, data });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.message || 'Failed to connect. Check URL or CORS settings.' });
    } finally {
      setIsTesting(false);
    }
  };

  const generateThumbnailAsync = async (formattedUrl: string, siteId: string, workspaceId: string) => {
    const isLocalUrl = formattedUrl.includes('.local') || formattedUrl.includes('localhost') || formattedUrl.includes('127.0.0.1');
    if (isLocalUrl) return;

    try {
      const imgRes = await fetch(`https://s0.wp.com/mshots/v1/${encodeURIComponent(formattedUrl)}?w=1200`);
      if (!imgRes.ok) return;
      const blob = await imgRes.blob();

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const fileName = `screenshot_${siteId}.jpg`;
      const r2Res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/r2-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          fileName,
          contentType: 'image/jpeg',
          folder: `sites/thumbnails/${workspaceId}`,
        }),
      });

      const r2Data = await r2Res.json();
      if (!r2Res.ok || !r2Data.url) return;

      const uploadRes = await fetch(r2Data.url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
      });

      if (uploadRes.ok && r2Data.publicUrl) {
        await supabase.from('workspace_sites').update({ thumbnail_url: r2Data.publicUrl }).eq('id', siteId);
        onSiteUpdated(siteId, { thumbnail_url: r2Data.publicUrl });
      }
    } catch (e) {
      console.error('Background thumbnail generation failed:', e);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative w-full max-w-2xl bg-card border border-border shadow-soft-xl rounded-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
              <h3 className="text-lg font-bold text-foreground">Connect WordPress Site</h3>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground bg-background rounded-full p-1 border border-border shadow-sm">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 md:p-8 overflow-y-auto">
              <p className="text-muted-foreground text-sm mb-6 pb-6 border-b border-border">
                Install the <a href="https://picvniwkpqtbykwgxvtl.supabase.co/functions/v1/agencyplus-connector-updates?action=download" download className="text-foreground font-bold hover:text-workspace-accent transition-colors underline decoration-workspace-accent/30 underline-offset-4">AgencyPlus Connector</a> plugin on your WordPress site, then enter these credentials to securely link it to your dashboard.
              </p>

              <form onSubmit={handleTestConnection} className="space-y-6">
                <div className="space-y-4 bg-muted/20 p-5 rounded-xl border border-border">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-semibold text-foreground">Authentication Keys</h4>
                    <button
                      type="button"
                      onClick={regenerateKeys}
                      className="text-xs font-medium text-workspace-accent hover:text-workspace-accent/80 flex items-center"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Regenerate Keys
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Public Key</label>
                    <div className="relative">
                      <input
                        type="text" readOnly
                        className="w-full bg-background border border-border rounded-md pl-3 pr-10 py-2 text-foreground focus:ring-1 focus:ring-workspace-accent outline-none font-mono text-xs cursor-text"
                        value={publicKey}
                      />
                      <button
                        type="button"
                        onClick={() => handleCopyKey(publicKey, 'copy-pub')}
                        id="copy-pub"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-background"
                        title="Copy Public Key"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground hover:text-foreground transition-colors"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Secret Key</label>
                    <div className="relative">
                      <input
                        type={showSecret ? 'text' : 'password'} readOnly
                        className="w-full bg-background border border-border rounded-md pl-3 pr-20 py-2 text-foreground focus:ring-1 focus:ring-workspace-accent outline-none font-mono text-xs cursor-text"
                        value={secretKey}
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-background pl-1">
                        <button type="button" onClick={() => setShowSecret(!showSecret)} className="p-1" title={showSecret ? 'Hide Secret Key' : 'Show Secret Key'}>
                          {showSecret ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground hover:text-foreground transition-colors"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground hover:text-foreground transition-colors"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyKey(secretKey, 'copy-sec')}
                          id="copy-sec"
                          className="p-1"
                          title="Copy Secret Key"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground hover:text-foreground transition-colors"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Site URL</label>
                  <input
                    type="url"
                    placeholder="https://your-site.com"
                    className="w-full bg-background border border-input rounded-lg px-4 py-2.5 text-foreground focus:ring-2 focus:ring-workspace-accent focus:border-transparent transition-all outline-none"
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isTesting || testResult?.success}
                  className="w-full flex items-center justify-center bg-workspace-accent hover:bg-workspace-accent-hover text-white font-medium rounded-lg px-4 py-3 transition-colors disabled:opacity-70"
                >
                  {isTesting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying Connection...</>
                  ) : (
                    <><Globe className="w-5 h-5 mr-2" /> Connect & Verify</>
                  )}
                </button>
              </form>

              {testResult && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 p-4 rounded-xl border flex flex-col gap-3 ${testResult.success ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400'}`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    {testResult.success ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    {testResult.message}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
